import { useEffect, useState } from "react";
import { Plus, Loader2 } from "lucide-react";
import api, { fmtINR, shortDate, formatDetail } from "@/lib/api";
import Modal from "@/components/Modal";

export default function AdminInvoices({ scope = "admin" }) {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [partners, setPartners] = useState([]);
    const [summary, setSummary] = useState(null);

    const load = async () => {
        const url = scope === "admin" ? "/admin/invoices" : "/partner/invoices";
        const r = await api.get(url);
        setItems(r.data.items);
        setSummary(r.data.summary);
        if (scope === "admin") {
            const p = await api.get("/admin/partners");
            setPartners(p.data.items);
        }
    };
    useEffect(() => { load();   }, [scope]);

    const markPaid = async (id) => {
        await api.post(`/admin/invoices/${id}/mark-paid`); load();
    };

    return (
        <div className="space-y-5">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">Invoices</h1>
                    <p className="text-sm text-slate-500 mt-1">Billing and settlements.</p>
                </div>
                {scope === "admin" && (
                    <button data-testid="invoice-new-btn" onClick={() => setOpen(true)} className="btn-primary px-4 py-2 rounded-md text-sm flex items-center gap-2"><Plus className="w-4 h-4" /> New invoice</button>
                )}
            </div>

            {summary && (
                <div className="grid grid-cols-2 gap-4">
                    <div className="card p-4"><div className="text-xs uppercase tracking-wider text-slate-500">Unpaid</div><div className="font-mono text-2xl mt-1">{fmtINR(summary.unpaid)}</div></div>
                    <div className="card p-4"><div className="text-xs uppercase tracking-wider text-slate-500">Paid</div><div className="font-mono text-2xl mt-1 text-emerald-600">{fmtINR(summary.paid)}</div></div>
                </div>
            )}

            <div className="card overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500">
                        <tr><th className="px-4 py-2.5 text-left">Invoice</th><th className="px-4 py-2.5 text-left">Partner</th>
                            <th className="px-4 py-2.5 text-left">Period</th><th className="px-4 py-2.5 text-right">Amount</th>
                            <th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5 text-left">Generated</th><th></th></tr>
                    </thead>
                    <tbody>
                        {items.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400">No invoices.</td></tr>}
                        {items.map((i) => (
                            <tr key={i.invoice_id} className="border-b border-slate-100 last:border-0">
                                <td className="px-4 py-3 font-mono text-xs">{i.invoice_id}</td>
                                <td className="px-4 py-3">{i.partner_name}</td>
                                <td className="px-4 py-3 text-xs">{i.period_from} → {i.period_to}</td>
                                <td className="px-4 py-3 text-right font-mono">{fmtINR(i.amount)}</td>
                                <td className="px-4 py-3 text-center"><span className={`pill ${i.status === "paid" ? "pill-success" : "pill-pending"}`}>{i.status}</span></td>
                                <td className="px-4 py-3 text-xs text-slate-500">{shortDate(i.generated_at)}</td>
                                <td className="px-4 py-3 text-right">
                                    {scope === "admin" && i.status === "unpaid" && (
                                        <button data-testid={`invoice-paid-${i.invoice_id}`} onClick={() => markPaid(i.invoice_id)} className="btn-emerald px-3 py-1.5 rounded-md text-xs">Mark paid</button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {open && <NewInvoiceModal partners={partners} onClose={() => setOpen(false)} onDone={() => { setOpen(false); load(); }} />}
        </div>
    );
}

function NewInvoiceModal({ partners, onClose, onDone }) {
    const [f, setF] = useState({ partner_id: "", period_from: "", period_to: "", amount: "", note: "" });
    const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
    const submit = async (e) => {
        e.preventDefault(); setErr(""); setBusy(true);
        try { await api.post("/admin/invoices", { ...f, amount: parseFloat(f.amount) }); onDone(); }
        catch (e) { setErr(formatDetail(e?.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    return (
        <Modal testId="invoice-new-modal" onClose={onClose} title="New invoice">
            <form onSubmit={submit} className="space-y-3">
                <div><label className="label">Partner</label>
                    <select data-testid="inv-partner" required className="input" value={f.partner_id} onChange={(e) => setF({ ...f, partner_id: e.target.value })}>
                        <option value="">Select…</option>
                        {partners.map((p) => <option key={p.partner_id} value={p.partner_id}>{p.business_name}</option>)}
                    </select></div>
                <div className="grid grid-cols-2 gap-3">
                    <div><label className="label">From</label><input data-testid="inv-from" required type="date" className="input" value={f.period_from} onChange={(e) => setF({ ...f, period_from: e.target.value })} /></div>
                    <div><label className="label">To</label><input data-testid="inv-to" required type="date" className="input" value={f.period_to} onChange={(e) => setF({ ...f, period_to: e.target.value })} /></div>
                </div>
                <div><label className="label">Amount (₹)</label><input data-testid="inv-amount" required type="number" step="0.01" min="0.01" className="input font-mono" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} /></div>
                <div><label className="label">Note</label><input data-testid="inv-note" className="input" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></div>
                {err && <div className="text-sm text-rose-600">{err}</div>}
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="btn-secondary px-4 py-2 rounded-md text-sm">Cancel</button>
                    <button data-testid="inv-submit" type="submit" disabled={busy} className="btn-primary px-4 py-2 rounded-md text-sm flex items-center gap-2">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Create</button>
                </div>
            </form>
        </Modal>
    );
}
